'use client';
import { Target, Palette, Code, BarChart3, ArrowRight, CheckCircle } from 'lucide-react';
import { useT } from '../../i18n';

const CustomSection = () => {
  const t = useT();

  const phases = [
    {
      week: t('custom.phase1.week'),
      title: t('custom.phase1.title'),
      description: t('custom.phase1.desc'),
      deliverable: t('custom.phase1.deliverable'),
      icon: <Target className="w-5 h-5" />,
    },
    {
      week: t('custom.phase2.week'),
      title: t('custom.phase2.title'),
      description: t('custom.phase2.desc'),
      deliverable: t('custom.phase2.deliverable'),
      icon: <Palette className="w-5 h-5" />,
    },
    {
      week: t('custom.phase3.week'),
      title: t('custom.phase3.title'),
      description: t('custom.phase3.desc'),
      deliverable: t('custom.phase3.deliverable'),
      icon: <Code className="w-5 h-5" />,
    },
    {
      week: t('custom.phase4.week'),
      title: t('custom.phase4.title'),
      description: t('custom.phase4.desc'),
      deliverable: t('custom.phase4.deliverable'),
      icon: <Code className="w-5 h-5" />,
    },
    {
      week: t('custom.phase5.week'),
      title: t('custom.phase5.title'),
      description: t('custom.phase5.desc'),
      deliverable: t('custom.phase5.deliverable'),
      icon: <BarChart3 className="w-5 h-5" />,
    },
  ];

  return (
    <section id="custom" className="py-12 lg:py-16 relative noise-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-2 bg-jkd-gray-100 text-jkd-gold text-sm font-mono tracking-widest mb-4 rounded">
            {t('custom.tag')}
          </span>
          <h2 className="section-title text-white">
            <span className="text-gradient-gold">{t('custom.title')}</span>
          </h2>
          <p className="section-subtitle mx-auto mt-4">
            {t('custom.subtitle')}
          </p>
          <div className="divider-gold mx-auto mt-6" />
        </div>

        {/* Custom Image */}
        <div className="max-w-2xl mx-auto mb-8">
          <img
            src="/images/eqw6pjpyow.png"
            alt="Custom Dojo"
            className="w-full h-auto rounded-lg"
          />
        </div>

        {/* Price Range - Fixed Responsive */}
        <div className="flex justify-center mb-12">
          <div className="w-full max-w-md px-6 py-5 bg-jkd-gray-100 rounded-lg border border-jkd-gray-200 text-center">
            <p className="text-gray-400 mb-2 text-sm">{t('custom.price')}</p>
            <div className="flex items-baseline justify-center gap-2 flex-wrap">
              <span className="text-2xl sm:text-3xl lg:text-4xl font-black text-jkd-gold">HK$15,000</span>
              <span className="text-xl text-gray-400">–</span>
              <span className="text-2xl sm:text-3xl lg:text-4xl font-black text-jkd-gold">HK$50,000</span>
            </div>
            <p className="text-gray-500 text-sm mt-2">{t('custom.priceNote')}</p>
          </div>
        </div>

        {/* Timeline - More Intuitive Card-based Design */}
        <div className="max-w-4xl mx-auto">
          {/* Progress Bar */}
          <div className="hidden md:flex items-center justify-between mb-8 px-4">
            {phases.map((phase, index) => (
              <div key={index} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-jkd-gold text-jkd-black flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <span className="text-xs text-gray-500 mt-2 font-mono">{phase.week}</span>
                </div>
                {index < phases.length - 1 && (
                  <div className="w-20 lg:w-32 h-0.5 bg-gradient-to-r from-jkd-gold to-jkd-gold/30 mx-2" />
                )}
              </div>
            ))}
          </div>

          {/* Phase Cards */}
          <div className="space-y-4">
            {phases.map((phase, index) => (
              <div
                key={index}
                className="group card-dark border-l-4 border-l-jkd-gold hover:bg-jkd-gray-100 transition-all duration-300"
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  {/* Phase Number - Mobile Only */}
                  <div className="sm:hidden flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-jkd-gold text-jkd-black flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {index + 1}
                    </div>
                    <span className="text-jkd-gold font-mono text-sm">{phase.week}</span>
                  </div>
                  
                  {/* Icon - Desktop Only */}
                  <div className="hidden sm:flex w-12 h-12 bg-jkd-gold/10 rounded-lg items-center justify-center text-jkd-gold flex-shrink-0">
                    {phase.icon}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="hidden sm:inline text-jkd-gold font-mono text-xs">{phase.week}</span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{phase.title}</h3>
                    <p className="text-gray-400 text-sm mb-3 leading-relaxed">{phase.description}</p>
                    <div className="inline-flex items-center gap-2 text-jkd-gold text-sm bg-jkd-black/50 px-3 py-1.5 rounded">
                      <CheckCircle className="w-4 h-4" />
                      <span>{phase.deliverable}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <a href="#contact" className="btn-gold text-lg inline-flex items-center gap-2">
            {t('custom.cta')}
            <ArrowRight className="w-5 h-5" />
          </a>
          <p className="text-gray-500 text-sm mt-4">{t('custom.ctaNote')}</p>
        </div>
      </div>
    </section>
  );
};

export default CustomSection;
